import React, { useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Button,
  TextField,
  InputAdornment,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import "./metric-card.scss";

/**
 * One record picker for every sensor. Each graph used to ship its own list
 * dialog with a different row shape; this replaces all seven.
 *
 * @param {array}    records   raw records, newest first
 * @param {function} getKey    record -> stable key
 * @param {function} getTitle  record -> primary line (usually the reading)
 * @param {function} getMeta   record -> secondary line (usually the timestamp)
 * @param {function} getSearchText  record -> text used by the filter box
 * @param {function} isSelected record -> bool
 */
const RecordDialog = ({
  open,
  onClose,
  onSelect,
  records = [],
  title = "Select a record",
  subtitle,
  accent,
  getKey = (r) => r.timestamp,
  getTitle = (r) => String(r.timestamp),
  getMeta = () => null,
  getSearchText,
  emptyLabel = "No records found",
  isSelected = () => false,
}) => {
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return records;
    return records.filter((record) => {
      const haystack = getSearchText
        ? getSearchText(record)
        : `${getTitle(record)} ${getMeta(record) ?? ""}`;
      return String(haystack).toLowerCase().includes(q);
    });
  }, [query, records, getSearchText, getTitle, getMeta]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      {/* component="div" because the subtitle below is block content, which is
          not valid inside the <h2> DialogTitle renders by default. */}
      <DialogTitle component="div">
        <h2 className="wg-card__title">{title}</h2>
        <p className="wg-card__sub">
          {subtitle ?? `Newest first · ${records.length} saved`}
        </p>
      </DialogTitle>

      <DialogContent dividers>
        {records.length > 6 && (
          <TextField
            className="record-dialog__search"
            fullWidth
            size="small"
            placeholder="Filter records"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        )}

        {filtered.length === 0 ? (
          <div className="record-dialog__empty">{emptyLabel}</div>
        ) : (
          <ul className="record-dialog__list">
            {filtered.map((record, index) => {
              const selected = isSelected(record);
              return (
                <li key={getKey(record)}>
                  <button
                    type="button"
                    className={`record-dialog__item ${selected ? "is-selected" : ""}`}
                    style={accent ? { "--metric-accent": accent } : undefined}
                    onClick={() => onSelect(record)}
                  >
                    <span className="record-dialog__item-head">
                      <span className="record-dialog__item-title">
                        {getTitle(record)}
                      </span>
                      <span className="record-dialog__tags">
                        {index === 0 && !query && (
                          <span className="wg-chip wg-chip--accent">Latest</span>
                        )}
                        {selected && <span className="wg-chip">Viewing</span>}
                      </span>
                    </span>
                    {getMeta(record) && (
                      <span className="record-dialog__item-meta">
                        {getMeta(record)}
                      </span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default RecordDialog;
